import { FC, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Divider, Drawer, FormControlLabel, IconButton, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateAccountSettings } from '../../store/accountThunk';
import { ALWAYS_UNIQUE, HIDEABLE_FIELDS, OPT_IN_FIELDS, PartyType, UNIQUE_FIELDS, usePartyFormConfig } from '../../utils/partyFormConfig';

interface Props {
    open: boolean;
    onClose: () => void;
    type: PartyType;
}

// Admin-only: choose which optional fields the Buyer/Supplier form shows and which must be unique.
const PartyFormConfigDrawer: FC<Props> = ({ open, onClose, type }) => {
    const dispatch = useAppDispatch();
    const accountId = useAppSelector((state: any) => state.auth.selectedAccount?._id);
    const { config, reload } = usePartyFormConfig(type);
    const [hidden, setHidden] = useState<string[]>([]);
    const [unique, setUnique] = useState<string[]>([]);
    const [gstTop, setGstTop] = useState(true);
    const [saving, setSaving] = useState(false);
    const label = type === 'supplier' ? 'Supplier' : 'Buyer';

    useEffect(() => { if (open) reload(); }, [open]);
    useEffect(() => { setHidden(config.hiddenFields); setUnique(config.uniqueFields); setGstTop(config.gstAutofillTop); }, [config]);

    const toggle = (list: string[], set: (v: string[]) => void, key: string) =>
        set(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await dispatch(updateAccountSettings({ accountId, partyForm: { [type]: { hiddenFields: hidden.filter((f) => !OPT_IN_FIELDS.includes(f)), optInFields: OPT_IN_FIELDS.filter((f) => !hidden.includes(f)), uniqueFields: unique, gstAutofillTop: gstTop } } })).unwrap();
            toast.success(`${label} form configuration saved`);
            onClose();
        } catch {
            toast.error('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 620 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <TuneIcon sx={{ color: '#4b5563' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', flex: 1 }}>{label} Form Configuration</Typography>
                <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                <FormControlLabel
                    control={<Switch size="small" checked={gstTop} onChange={(e) => setGstTop(e.target.checked)} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>Show GSTIN auto-fill at top</Typography>}
                    sx={{ ml: 0, gap: 1 }}
                />
                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 1 }}>Off: the GST field stays in Registration &amp; Legal (useful when most {label.toLowerCase()}s have no GST).</Typography>
                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>Fields shown in form</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>Name, Legal Name, Billing Address and GST are always shown.</Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                    {HIDEABLE_FIELDS[type].map((f) => (
                        <Box key={f.key} sx={row}>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={!hidden.includes(f.key)} onChange={() => toggle(hidden, setHidden, f.key)} />}
                                label={<Typography variant="body2">{f.label}</Typography>}
                            />
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>Must be unique</Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>A new {label.toLowerCase()} is rejected if one of these matches an existing party. GST is always unique.</Typography>
                <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                    {UNIQUE_FIELDS.map((f) => {
                        const locked = ALWAYS_UNIQUE.includes(f.key);
                        return (
                            <Box key={f.key} sx={row}>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={locked || unique.includes(f.key)} disabled={locked} onChange={() => toggle(unique, setUnique, f.key)} />}
                                    label={<Typography variant="body2">{f.label}</Typography>}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 1.25, backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                <Button onClick={onClose} variant="outlined" sx={{ height: 34, textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}>Cancel</Button>
                <LoadingButton onClick={handleSave} loading={saving} variant="contained" sx={{ height: 34, textTransform: 'none', fontWeight: 600 }}>Save</LoadingButton>
            </Box>
        </Drawer>
    );
};

export default PartyFormConfigDrawer;
